import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import { styled } from "@mui/material";
import LoadingButton from "_shared/_views/loading/loading-button";
import Guid from "_shared/guids/guid";
import {
  useCreateCompanyBookmarkMutation,
  useDeleteCompanyBookmarkMutation,
  useGetAllCompanyBookmarksQuery,
} from "marketplace/bookmarks/_api/bookmark-api";
import { useSnackbar } from "notistack";

const StyledBookmarkButton = styled(LoadingButton)<{ selected: boolean }>(({ selected, theme }) => ({
  backgroundColor: selected ? theme.palette.primary.main : undefined,
  color: selected ? theme.palette.primary.contrastText : undefined,
  marginBottom: theme.spacing(1),
  marginLeft: theme.spacing(2),
  minWidth: theme.spacing(24),
  whiteSpace: "nowrap",
  "&:hover": {
    backgroundColor: selected ? theme.palette.primary.main : undefined,
    color: selected ? theme.palette.primary.contrastText : undefined,
  },
}));

export interface CompanyBookmarkButtonProps {
  className?: string;
  companyId?: Guid;
}

export default function CompanyBookmarkButton(props: Readonly<CompanyBookmarkButtonProps>) {
  const { className, companyId } = props;

  const { enqueueSnackbar } = useSnackbar();

  const { data: companyBookmarks, isLoading: bookmarksLoading } = useGetAllCompanyBookmarksQuery();
  const [createCompanyBookmark, { isLoading: isCreating }] = useCreateCompanyBookmarkMutation();
  const [deleteCompanyBookmark, { isLoading: isDeleting }] = useDeleteCompanyBookmarkMutation();

  const existingBookmark = companyBookmarks?.find((b) => b.companyId === companyId?.value);
  const isLoading = bookmarksLoading || isCreating || isDeleting;

  const handleBookmarkClick = async () => {
    try {
      if (existingBookmark) {
        await deleteCompanyBookmark({ bookmarkId: existingBookmark.id }).unwrap();
        enqueueSnackbar("Removed bookmark", { variant: "info" });
      } else {
        if (!companyId) throw new Error("Bookmark Company: Company ID is undefined");
        await createCompanyBookmark({ companyId: companyId.value }).unwrap();
        enqueueSnackbar("Added bookmark", { variant: "info" });
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Couldn't save bookmark", { variant: "error" });
    }
  };

  return (
    <StyledBookmarkButton
      className={className}
      selected={existingBookmark !== undefined}
      variant="outlined"
      aria-label="Bookmark"
      loading={isLoading}
      startIcon={<BookmarkBorderIcon />}
      disableElevation
      disabled={!companyId}
      onClick={handleBookmarkClick}
    >
      {existingBookmark ? "Bookmarked" : "Bookmark"}
    </StyledBookmarkButton>
  );
}
